<template>
  <a-modal
    :visible="visible"
    :width="1240"
    :footer="null"
    :destroy-on-close="true"
    :closable="false"
    wrap-class-name="qv-window-wrap"
    @cancel="$emit('close')"
  >
    <div class="qv-window">
      <qv-header
        :product="product"
        :updated-at="updatedAt"
        :stores="stores"
        :selected-store-id="selectedStoreId"
        :compact-store-select="true"
        :store-locked="storeLocked"
        @change-store="handleStoreChange"
      >
        <template #actions>
          <div class="qv-corner-actions">
            <template v-if="hasNavigation">
              <a-tooltip title="Produto anterior">
                <a-button
                  class="qv-icon-btn"
                  type="link"
                  icon="left"
                  size="small"
                  :disabled="!canGoPrevious"
                  @click="$emit('previous-product')"
                />
              </a-tooltip>

              <span class="qv-nav-counter">{{ currentIndex + 1 }}/{{ totalCount }}</span>

              <a-tooltip title="Próximo produto">
                <a-button
                  class="qv-icon-btn"
                  type="link"
                  icon="right"
                  size="small"
                  :disabled="!canGoNext"
                  @click="$emit('next-product')"
                />
              </a-tooltip>
            </template>

            <a-tooltip title="Voltar para o painel lateral">
              <a-button
                class="qv-icon-btn"
                type="link"
                icon="fullscreen-exit"
                size="small"
                @click="$emit('collapse')"
              />
            </a-tooltip>

            <a-tooltip title="Fechar">
              <a-button
                class="qv-icon-btn"
                type="link"
                icon="close"
                size="small"
                @click="$emit('close')"
              />
            </a-tooltip>
          </div>
        </template>
      </qv-header>

      <div class="qv-content">
        <div class="qv-top-row">
          <qv-last-purchase v-if="!dataUnavailable" :loading="loading" :last-purchase="lastPurchase" />

          <qv-preorder-actions
            class="qv-preorder-float"
            :loading="loading"
            :quantity="requestedQuantity"
            :packaging="product && product.packing"
            :can-edit-packaging="canEditPackaging"
            :read-only="readOnly"
            :quantity-locked="quantityLocked"
            @update-quantity="$emit('update-quantity', $event)"
            @edit-packaging="$emit('edit-packaging')"
          />
        </div>

        <div v-if="dataUnavailable" class="qv-no-data">
          <span class="material-icons-outlined">info</span>
          Sem dados de giro disponíveis — este produto não tem código interno cadastrado.
        </div>

        <template v-else>
          <div class="qv-stats-grid">
            <qv-stock-summary :loading="loading" :stock="stock" />
            <qv-last-sale :loading="loading" :last-sale="lastSale" />
            <qv-sales-analysis class="qv-stat-span-2" :loading="loading" :sales-analysis="salesAnalysis" />
          </div>

          <qv-tabs
            :loading="loading"
            :purchases="purchases"
            :network-sales="networkSales"
            :network-total="networkTotal"
            :sales-points="salesPoints"
            :purchase-points="purchasePoints"
            :diagnostics-stores="diagnosticsStores"
          />
        </template>
      </div>
    </div>
  </a-modal>
</template>

<script>
import QvHeader from './product-quick-view/_qv-header.vue';
import QvPreorderActions from './product-quick-view/_qv-preorder-actions.vue';
import QvLastPurchase from './product-quick-view/_qv-last-purchase.vue';
import QvStockSummary from './product-quick-view/_qv-stock-summary.vue';
import QvLastSale from './product-quick-view/_qv-last-sale.vue';
import QvSalesAnalysis from './product-quick-view/_qv-sales-analysis.vue';
import QvTabs from './product-quick-view/_qv-tabs.vue';

export default {
    name: 'ProductQuickViewWindow',

    components: {
        QvHeader,
        QvPreorderActions,
        QvLastPurchase,
        QvStockSummary,
        QvLastSale,
        QvSalesAnalysis,
        QvTabs,
    },

    props: {
        updatedAt: {
            type: String,
            default: '',
        },

        diagnosticsStores: {
            type: Array,
            default: () => [],
        },

        loading: {
            type: Boolean,
            default: false,
        },

        visible: {
            type: Boolean,
            default: false,
        },

        product: {
            type: Object,
            default: null,
        },

        requestedQuantity: {
            type: [String, Number],
            default: 0,
        },

        lastPurchase: {
            type: Object,
            default: null,
        },

        stock: {
            type: Object,
            default: () => ({}),
        },

        lastSale: {
            type: Object,
            default: null,
        },

        salesAnalysis: {
            type: Array,
            default: () => [],
        },

        salesPoints: {
            type: Array,
            default: () => [],
        },

        purchasePoints: {
            type: Array,
            default: () => [],
        },

        purchases: {
            type: Array,
            default: () => [],
        },

        networkSales: {
            type: Array,
            default: () => [],
        },

        networkTotal: {
            type: Object,
            default: () => ({
                average: 0,
                total: 0,
            }),
        },

        stores: {
            type: Array,
            default: () => [],
        },

        hasNavigation: {
            type: Boolean,
            default: false,
        },

        canGoPrevious: {
            type: Boolean,
            default: false,
        },

        canGoNext: {
            type: Boolean,
            default: false,
        },

        currentIndex: {
            type: Number,
            default: 0,
        },

        totalCount: {
            type: Number,
            default: 0,
        },

        selectedStoreId: {
            type: [String, Number],
            default: null,
        },

        storeLocked: {
            type: Boolean,
            default: false,
        },

        readOnly: {
            type: Boolean,
            default: false,
        },

        quantityLocked: {
            type: Boolean,
            default: false,
        },

        dataUnavailable: {
            type: Boolean,
            default: false,
        },
    },

    computed: {
        canEditPackaging() {
            return !!this.product &&
                !this.product.is_generic &&
                !this.product.main_product_id;
        },
    },

    methods: {
        handleStoreChange(storeId) {
            this.$emit('change-store', storeId);
        },
    },
};
</script>

<style lang="scss" scoped>
@import './product-quick-view/_qv-shared.scss';

.qv-window {
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 80px);
  width: 100%;
  color: var(--text);
  font-family: Inter, -apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif;
}

.qv-corner-actions {
  display: flex;
  align-items: center;
  gap: 6px;
}

.qv-icon-btn {
  flex-shrink: 0;
  color: var(--text-3);
  padding: 0 2px;
}

.qv-icon-btn:hover {
  color: var(--brand-deep);
}

.qv-nav-counter {
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 600;
  color: var(--text-3);
  white-space: nowrap;
}

.qv-window ::v-deep .qv-head {
  flex-shrink: 0;
}

.qv-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 20px;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

.qv-top-row {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 16px;
  align-items: start;
  flex-shrink: 0;
}

.qv-preorder-float {
  width: 300px;
}

.qv-stats-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  flex-shrink: 0;
}

.qv-stat-span-2 {
  grid-column: span 2;
}
</style>

<style lang="scss">
.qv-window-wrap {
  .ant-modal {
    top: 40px;
  }

  .ant-modal-content {
    padding: 0;
    border-radius: 10px;
    overflow: hidden;
  }

  .ant-modal-body {
    padding: 0;
  }
}
</style>
